import { axisClasses } from "@mui/x-charts/ChartsAxis";
import { i18n } from "@lingui/core";

type CartesianChartData = {
  xLabel: string;
  yLabel: string;
  abbreviatedContent: Array<{ [key: string]: any }>;
  series: { dataKey: string; label: string; color: string }[];
};

export const getCartesianChartData = (
  content: Array<{ [key: string]: any }>,
  xAxisKey: string,
  colorPalette: Array<string>,
  yAxisKey?: string,
): CartesianChartData => {
  function abbreviateForDisplay(value: any, max = 4): string {
    const v = String(value ?? "").trim();
    if (!v) return "";

    // ISO-like date e.g. 2024-xx-xx or 2024-05-xx
    const m = v.match(/^(\d{4})-(\d{2}|xx)-(?:\d{2}|xx)$/i);
    if (m) {
      const [_, year, month] = m;
      return month.toLowerCase() === "xx" ? year : `${year}-${month}`;
    }

    // Real date
    const d = new Date(v);
    if (!isNaN(d.valueOf())) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; // YYYY-MM
    }

    // Normal text → try initials or first word
    const parts = v.split(/[\s_]+/);
    if (parts.length > 1 && parts[0].length <= max) return parts[0];
    if (parts.length > 1)
      return parts
        .map((p) => p[0])
        .join("")
        .slice(0, max)
        .toUpperCase();

    // Fallback: truncate
    return v.slice(0, max);
  }

  // keep a copy of the full label, and add a shortened one for ticks
  const abbreviatedContent = content.map((item) => ({
    ...item,
    [`__full_${xAxisKey}`]: item[xAxisKey], // full label
    [xAxisKey]: abbreviateForDisplay(item[xAxisKey]), // human-readable abbreviation
  }));

  const xLabel = toTitleCase(xAxisKey);
  const yLabel = toTitleCase(yAxisKey || "");

  const series = Object.keys(abbreviatedContent[0])
    .filter((key) => key !== xAxisKey && key !== `__full_${xAxisKey}`)
    .map((key, index) => ({
      dataKey: key,
      label: toTitleCase(key),
      color: colorPalette[index % colorPalette.length],
    }));

  return { xLabel, yLabel, abbreviatedContent, series };
};

function capitalizeFirstLetter(val: string): string {
  if (val.length > 1) {
    return (
      String(val).charAt(0).toUpperCase() + String(val).slice(1).toLowerCase()
    );
  }
  return val.toUpperCase();
}

export const toTitleCase = (str: string): string => {
  return str
    .split("_")
    .map((word) => capitalizeFirstLetter(word))
    .join(" ");
};

export const defaultColorPalette = [
  "#A78D75", // dark pastel brown
  "#A3A49A", // dark pastel olive-gray
  "#6E4F75", // dark pastel mauve
  "#4E6D5C", // dark pastel green
  "#5A4B66", // dark pastel lavender-gray
];

export const cartesianChartStyling = {
  sx: {
    [`.${axisClasses.left} .${axisClasses.label}`]: {
      transform: "translate(-40px, 0)",
    },
  },
};

// Value formatter context compatible with MUI X Charts AxisValueFormatterContext
export type ValueFormatterContext =
  | { location: "legend" }
  | { location: "tick" | "tooltip"; scale: unknown };

// Creates a value formatter that shows abbreviated labels on ticks,
// and expands to full labels in tooltip when available.
export function makeAbbrevValueFormatter(fullByAbbrev: Map<string, string>) {
  return (val: any, ctx: ValueFormatterContext): string => {
    if (ctx.location === "tick") return String(val);
    if (ctx.location === "tooltip")
      return fullByAbbrev.get(String(val)) ?? String(val);
    return String(val);
  };
}

// Build a quick lookup from abbreviated label -> full label
export function buildFullByAbbrevMap(
  abbreviatedContent: Array<{ [key: string]: any }>,
  xAxisKey: string,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const d of abbreviatedContent) {
    const abbr = String(d[xAxisKey]);
    const full = d[`__full_${xAxisKey}`];
    if (full != null) map.set(abbr, String(full));
  }
  return map;
}

// ---------------------------
// Y-axis / numeric formatters
// ---------------------------

export function makeNumericValueFormatter(opts?: {
  ticks?: "compact" | "full";
  tooltipMaxFractionDigits?: number;
  fullMaxFractionDigits?: number;
}) {
  const {
    ticks = "compact",
    tooltipMaxFractionDigits,
    fullMaxFractionDigits = 2,
  } = opts ?? {};

  return (val: any, ctx: ValueFormatterContext): string => {
    if (val == null || val === "") return "";

    if (ctx.location === "tick") {
      return ticks === "compact"
        ? i18n.number(val, {
            notation: "compact",
            maximumFractionDigits: 2,
          })
        : i18n.number(val, {
            style: "decimal",
            maximumFractionDigits: fullMaxFractionDigits,
          });
    }

    // Tooltip or legend → show full number
    return i18n.number(val, {
      style: "decimal",
      maximumFractionDigits: tooltipMaxFractionDigits ?? fullMaxFractionDigits,
    });
  };
}
